import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Flex,
  GridItem,
  HStack,
  Heading,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';

import { Card } from '@/imports/ui/chakra/components/Card';

import { ProgressUploadCard } from './components/ProgressUploadCard';
import { ResourceCard } from './components/ResourceCard';
import { IEventResource, MAX_NUMBER_OF_RESOURCES } from './constants';

const INCREMENT_NUM = 6;

type ISharedResourcesCardViewProps = {
  canCurrentUserEditResources: boolean;
  isRemoving: boolean;
  isPrepping: boolean;
  isProcessing: boolean; // includes all 3 steps (1. prep, 2. upload to s3, 3. add)
  onRemoveResource: (resourceId: string) => void;
  onUploadResource: (file: File) => Promise<void>;
  allResources?: Array<IEventResource>;
  uploadProgress?: number;
  refetchResources: () => void;
  resourcesLoading: boolean;
};

export const SharedResourcesCardView: React.FC<
  ISharedResourcesCardViewProps
> = ({
  allResources,
  canCurrentUserEditResources,
  isRemoving,
  isPrepping,
  isProcessing,
  onRemoveResource,
  onUploadResource,
  uploadProgress,
  refetchResources,
  resourcesLoading,
}) => {
  const [files, setFiles] = React.useState<File[]>([]);

  const [numfilesToShow, setNumfilesToShow] = React.useState(INCREMENT_NUM);

  const handleDrop = async (acceptedFiles: File[]): Promise<void> => {
    const file = acceptedFiles && acceptedFiles.length > 0 && acceptedFiles[0];
    if (!file) {
      setFiles([]);
      return;
    }

    setFiles([file]);

    await onUploadResource(file);
  };

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: handleDrop,
    multiple: false,
  });

  const getProgressValue = (): number | undefined => {
    /**
     * The process to upload a video has 3 steps:
     * - prepping
     * - uploading
     * - adding
     */
    if (isPrepping) {
      return 0;
    } else {
      return uploadProgress;
    }
  };

  const resourcesCount = allResources?.length ?? 0;
  const isProcessingFirstResource = !resourcesCount && isProcessing;

  return (
    <Card p="6">
      <HStack justify="space-between">
        <Box>
          <Heading size="sm" lineHeight="base">
            Shared Documents
          </Heading>

          {canCurrentUserEditResources && (
            <Text color="gray.600">
              Share documents with your event attendees.
            </Text>
          )}
        </Box>

        <ButtonGroup>
          <Button
            variant="outline"
            colorScheme="gray"
            isLoading={isProcessing || resourcesLoading}
            onClick={() => refetchResources()}
          >
            Refresh
          </Button>
          {canCurrentUserEditResources && (
            <Button
              disabled={
                isProcessing || resourcesCount === MAX_NUMBER_OF_RESOURCES
              }
              onClick={open}
            >
              Upload File
            </Button>
          )}
        </ButtonGroup>
      </HStack>

      {canCurrentUserEditResources && (
        <Flex
          align="center"
          direction="column"
          justify={!!allResources?.length ? 'flex-start' : 'center'}
          display={
            !!allResources?.length || isProcessingFirstResource
              ? 'none'
              : 'flex'
          }
          border="1px"
          borderColor={isDragActive ? 'gray.300' : 'transparent'}
          bg="gray.50"
          borderRadius="base"
          p="3"
          mt="6"
          mx="auto"
          minHeight="112px"
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Center h="full" textAlign="center">
            <Text fontWeight="semibold" color="gray.600">
              {isDragActive ? 'Drop the document here' : 'Drag & Drop'}
            </Text>
          </Center>
        </Flex>
      )}

      {(resourcesCount > 0 || isProcessingFirstResource) && (
        <SimpleGrid pt="6" minChildWidth="360px" gap="2">
          {allResources?.slice(0, numfilesToShow).map((resource) => {
            return (
              <GridItem key={resource._id}>
                <ResourceCard
                  resource={resource}
                  canUserEdit={canCurrentUserEditResources}
                  onRemoveResource={onRemoveResource}
                  isRemoving={isRemoving}
                />
              </GridItem>
            );
          })}

          {isProcessing &&
            files.map((file) => {
              return (
                <GridItem key={file.name}>
                  <ProgressUploadCard
                    key={file.name}
                    value={getProgressValue()}
                  />
                </GridItem>
              );
            })}
        </SimpleGrid>
      )}

      {numfilesToShow < resourcesCount && (
        <Center pt="6">
          <Button
            variant="link"
            colorScheme="black"
            onClick={() =>
              setNumfilesToShow(
                (numfilesToShow) => numfilesToShow + INCREMENT_NUM
              )
            }
          >
            See More Documents
          </Button>
        </Center>
      )}
    </Card>
  );
};
